/* AUTO-SYNCED from PsyKit.io/components/brand/Brandmark.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
import type { CSSProperties } from "react";

type BrandmarkProps = {
  /** Rendered pixel size (width = height). Defaults to 36. */
  size?: number;
  /** Surrounding frame shape. "circle" (default) or rounded "square". */
  variant?: "circle" | "square";
  className?: string;
};

/**
 * Psy180 brand mark — the slanted Ψ, optically centered inside a gold frame.
 *
 * Single source of truth: every placement (nav, app header, auth pages,
 * session card) renders identical pixels and scales cleanly via the 100×100
 * viewBox. Replaces the old per-page `<span>Ψ</span>` marks that drifted.
 *
 * The Ψ is nudged to x=47.65 (not 50) because the italic shear pushes the
 * glyph's *ink* right of its advance-width box; the offset was measured with
 * getBBox() so the visible letter sits dead-center on both axes. Font-size 56
 * gives even breathing room to the round ring.
 */
export function Brandmark({ size = 36, variant = "circle", className }: BrandmarkProps) {
  const frameStyle: CSSProperties =
    variant === "circle"
      ? { fill: "var(--color-bg-deep)", stroke: "var(--color-gold)" }
      : { fill: "var(--color-navy-mid)", stroke: "var(--color-border-gold)" };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {variant === "circle" ? (
        <circle cx="50" cy="50" r="44" strokeWidth="3" style={frameStyle} />
      ) : (
        <rect x="3" y="3" width="94" height="94" rx="11" strokeWidth="2.5" style={frameStyle} />
      )}
      <text
        x="47.65"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="56"
        style={{
          fontFamily: "var(--font-cormorant), Georgia, 'Times New Roman', serif",
          fontStyle: "italic",
          fontWeight: 400,
          fill: "var(--color-gold-soft)",
        }}
      >
        Ψ
      </text>
    </svg>
  );
}
